'use client'

import { useTranslations } from 'next-intl'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { AppFormDescription } from '@/components/forms/AppFormDescription'
import { ComboboxArrow } from '@/components/icons/social/ComboboxArrow'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/styles'

type Props = {
	name: string
	values: { value: string; label: string }[]
	noDataMessage?: string
	placeholder?: string
	label?: ReactNode
	description?: ReactNode
	containerClassName?: string
	fieldClassName?: string
	popoverClassName?: string
	required?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>

export function AppCombobox({
	name,
	values,
	noDataMessage,
	placeholder,
	description,
	containerClassName,
	fieldClassName,
	popoverClassName,
}: Props) {
	const t = useTranslations('AppCombobox')
	const { control } = useFormContext()

	const toggleValue = (current: string, selected: string[]) => {
		return selected.includes(current)
			? selected.filter((v) => v !== current)
			: [...selected, current]
	}

	return (
		<FormField
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FormItem className={cn(containerClassName)}>
					<Popover>
						<PopoverTrigger asChild>
							<FormControl>
								<Button
									variant="none"
									size="none"
									aria-expanded={field.value?.length > 0}
									className={cn(
										'w-[180px] bg-white justify-between overflow-hidden text-ellipsis border border-neutral-light-600 rounded-sm p-3',
										fieldClassName,
										fieldState.invalid && 'border-system-critical',
									)}
								>
									<Paragraph
										fontSize="bodyRegularMedium"
										className="flex justify-between w-full items-center"
									>
										{field.value?.length > 0
											? t('selected', { count: field.value.length })
											: placeholder || t('placeholder')}
										<ComboboxArrow />
									</Paragraph>
								</Button>
							</FormControl>
						</PopoverTrigger>

						<PopoverContent className={cn('w-[180px] p-0 z-10', popoverClassName)} side="bottom">
							<Command>
								<CommandList>
									<CommandEmpty>{noDataMessage || t('noDataMessage')}</CommandEmpty>
									<CommandGroup>
										{values.map((item) => (
											<CommandItem
												key={item.value}
												value={item.value}
												onSelect={() => field.onChange(toggleValue(item.value, field.value ?? []))}
												active={field.value?.includes(item.value)}
											>
												{item.label}
											</CommandItem>
										))}
									</CommandGroup>
									{(field.value?.length ?? 0) > 0 && (
										<CommandItem
											value="__clear_all__"
											onSelect={() => field.onChange([])}
											className="text-destructive pl-10"
											hideCheckIcon
										>
											{t('clearSelection')}
										</CommandItem>
									)}
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>

					<AppFormDescription description={description} />
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
